<template>
  <InputText autocomplete="off" ref="inputRef" class="sr-input-number" :type="inputType" v-model="stringValue"
    @keydown="onKeyDown" @paste="onPaste" @input="onInput" @blur="onBlur" :placeholder="placeholder" :fluid="fluid">
  </InputText>
</template>

<script setup lang="ts">
// import Decimal from 'decimal.js';
// import { isNull, isUndefined } from 'lodash';
import InputText from 'primevue/inputtext'
import { watch, ref, computed, nextTick } from 'vue';

// options based
const emit = defineEmits<{
  (e: 'change', v: number | null | undefined): void
}>()
const inputType = ref('text')

const props = withDefaults(defineProps<{
  placeholder?: string
  max?: number | null
  step?: number | null
  min?: number | null
  maxFractionDigits?: number
  fluid?: boolean
}>(), {
  maxFractionDigits: 20
})

const stepDigits = computed(() => {
  if (props.maxFractionDigits) {
    return props.maxFractionDigits
  }
  const digits = props.step?.toString()
  if (!digits) return 0
  if (digits.includes('e')) {
    return Number(digits.split('e')[1])
  }
  return digits.split('.')[1]?.length ?? 0
})
// External number model (binds to parent's v-model:number)
const numberModel = defineModel<number | null | undefined>()

// Internal raw string storage (preserves partial input like "123.")
const rawString = ref<string>(numberModel.value?.toString() ?? '')

// Computed for v-model that syncs raw string to numberModel
const stringValue = computed<string>({
  get: () => rawString.value,
  set: (value: string) => {
    rawString.value = value // Always preserve raw input

    // Sync to external number model
    if (value === '' || value === '-') {
      numberModel.value = null
      return
    }
    const numericValue = Number(value)

    if (!Number.isNaN(numericValue)) {
      numberModel.value = numericValue
    }
  }
})

// Watch external model to update internal string when parent changes
watch(() => numberModel.value, (newVal) => {
  emit('change', newVal)
  const newStr = newVal?.toString() ?? ''
  // Only update if numeric value differs (prevents infinite loops and preserves partial input)
  if (Number(rawString.value) !== newVal) {
    rawString.value = newStr
  }
}, { immediate: true })

const inputRef = ref<InstanceType<typeof InputText> | null>(null)

const allowedKeys = new Set([
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
])

const controlKeys = new Set(['a', 'c', 'v', 'x', 'r'])

const getInputElement = (): HTMLInputElement | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (inputRef.value as any)?.$el as HTMLInputElement | undefined
}

const onKeyDown = (e: KeyboardEvent) => {
  const key = e.key
  const inputElement = getInputElement()
  if (!inputElement) return

  const value = rawString.value
  const start = inputElement.selectionStart || 0
  const end = inputElement.selectionEnd || 0
  const hasSelection = start !== end
  const dotIndex = value.indexOf('.')

  // Allow browser shortcuts
  if ((e.ctrlKey || e.metaKey) && controlKeys.has(key)) return

  // Allow navigation and function keys
  if (allowedKeys.has(key) || key.length > 1) return

  // Allow digits
  if (key >= '0' && key <= '9') {
    // Block typing if cursor is in decimal part AND it would exceed max length
    if (dotIndex !== -1 && start > dotIndex && !hasSelection) {
      const decimalLength = value.substring(dotIndex + 1).length
      if (decimalLength >= stepDigits.value) {
        e.preventDefault()
        return
      }
    }
    return // Allow digit
  }

  // Handle minus sign (only at start or when selecting all)
  if (key === '-') {
    const selectingAll = hasSelection && start === 0 && end === value.length
    if (selectingAll || (start === 0 && !value.startsWith('-'))) {
      return // Allow default behavior
    }
    e.preventDefault()
    return
  }

  // Handle decimal separator
  if (key === '.' || key === ',') {
    e.preventDefault()

    // Block if decimal exists OR stepDigits is 0
    if (value.includes('.') || stepDigits.value === 0) return

    const newValue = value.substring(0, start) + '.' + value.substring(end)
    stringValue.value = newValue // This updates rawString and syncs to numberModel
    nextTick(() => inputElement.setSelectionRange(start + 1, start + 1))
    return
  }

  // Block anything else
  e.preventDefault()
}

const onPaste = (e: ClipboardEvent) => {
  e.preventDefault()

  const inputElement = getInputElement()
  if (!inputElement) return

  const pastedText = e.clipboardData?.getData('text') || ''
  const start = inputElement.selectionStart || 0
  const end = inputElement.selectionEnd || 0
  const currentValue = rawString.value
  const hasSelection = start !== end

  // 1. Clean and normalize
  let fragment = pastedText.replace(/[^0-9.,-]/g, '').replace(/,/g, '.')

  // 2. Handle minus sign
  const fragmentMinusPos = fragment.indexOf('-')
  if (fragmentMinusPos !== -1) {
    const selectingAll = hasSelection && start === 0 && end === currentValue.length
    if (fragmentMinusPos !== 0 ||
      (!selectingAll && (start !== 0 || currentValue.startsWith('-')))) {
      fragment = fragment.replace(/-/g, '')
    } else {
      fragment = '-' + fragment.replace(/-/g, '')
    }
  }

  // 3. Ensure one dot max in fragment
  const firstDotIndex = fragment.indexOf('.')
  if (firstDotIndex !== -1) {
    fragment = fragment.substring(0, firstDotIndex + 1) +
      fragment.substring(firstDotIndex + 1).replace(/\./g, '')
  }

  // 4. Remove dot if main value already has one
  if (currentValue.includes('.') && fragment.includes('.')) {
    fragment = fragment.replace('.', '')
  }

  // 5. Apply maxFractionDigits to fragment
  if (fragment.includes('.') && stepDigits.value > 0) {
    const [intPart, decPart] = fragment.split('.')
    if (decPart && decPart.length > stepDigits.value) {
      fragment = `${intPart}.${decPart.substring(0, stepDigits.value)}`
    }
  }

  // 6. Insert
  stringValue.value = currentValue.substring(0, start) + fragment + currentValue.substring(end)

  // 7. Restore cursor
  nextTick(() => {
    inputElement.setSelectionRange(start + fragment.length, start + fragment.length)
  })
}

const onInput = (e: Event) => {
  const inputElement = e.target as HTMLInputElement
  const cursorPos = inputElement.selectionStart || 0
  const originalValue = rawString.value
  if (!originalValue) {
    return
  }
  // Normalize and validate
  let sanitized = originalValue.replace(/,/g, '.')

  // Validate minus sign
  const hasLeadingMinus = sanitized.startsWith('-')
  sanitized = sanitized.replace(/-/g, '')
  if (hasLeadingMinus) sanitized = '-' + sanitized

  // Ensure only one decimal point
  const dotParts = sanitized.split('.')
  if (dotParts.length > 2) {
    const decimals = dotParts.slice(1).join('')
    sanitized = dotParts[0] + '.' + decimals
  }

  // Limit decimal digits during input
  if (dotParts.length === 2 && stepDigits.value) {
    const decimals = dotParts[1]
    if (decimals && decimals.length > stepDigits.value) {
      sanitized = dotParts[0] + '.' + decimals.slice(0, stepDigits.value)
    }
  }

  if (sanitized !== originalValue) {
    stringValue.value = sanitized // This triggers the sync
    const removedChars = originalValue.length - sanitized.length
    nextTick(() => {
      inputElement.setSelectionRange(Math.max(0, cursorPos - removedChars), Math.max(0, cursorPos - removedChars))
    })
  }
}

// const fixByRules = () => {
//   const numericValue = numberModel.value as number
//   if (numberModel.value) {
//     if (!isNull(props.max) && !isUndefined(props.max) && numericValue > props.max) {
//       numberModel.value = props.max
//       rawString.value = props.max.toString()
//     } else if (!isNull(props.min) && !isUndefined(props.min) && numericValue < props.min) {
//       numberModel.value = props.min
//       rawString.value = props.min.toString()
//     }
//     if (!isNull(props.step) && !isUndefined(props.step)) {
//       const decValue = new Decimal(numericValue)
//       const decStep = new Decimal(props.step)
//       numberModel.value = decValue.div(decStep).floor().mul(decStep).toNumber()
//       rawString.value = numberModel.value?.toString() ?? ''
//     }
//   }
// }

const onBlur = () => {
  // fixByRules()
  // Optional: format on blur (e.g., "123." -> "123")
  if (rawString.value === '.' || rawString.value === '-') {
    rawString.value = ''
    numberModel.value = null
  }
}
</script>
